import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Users, 
  CheckCircle, 
  CurrencyDollar, 
  Link as LinkIcon,
  House,
  ChartLineUp,
  Handshake,
  Medal,
  ArrowRight,
  MapPin
} from '@phosphor-icons/react';

const AFFILIATE_TYPES = [
  { title: 'Realtors', description: 'Connect clients to monetize their spaces', icon: House },
  { title: 'Interior Designers', description: 'Help clients earn from designed spaces', icon: Medal },
  { title: 'Basement Contractors', description: 'Turn renovations into revenue', icon: Handshake },
  { title: 'Home Gym Installers', description: 'Gyms that pay for themselves', icon: Users },
  { title: 'Pool Companies', description: 'Pools as profit centers', icon: ChartLineUp },
  { title: 'Community Influencers', description: 'Monetize your local network', icon: Users },
  { title: 'HOA Leaders', description: 'Help homeowners earn', icon: House },
  { title: 'Facebook Group Admins', description: 'Turn your community into cash', icon: LinkIcon },
  { title: 'Local Creators', description: 'Content meets commerce', icon: Medal },
];

const FAQ_ITEMS = [
  {
    question: 'Do I refer guests?',
    answer: 'No. This program is for referring hosts only. You earn when your referred hosts get bookings.'
  },
  {
    question: 'How long do I earn?',
    answer: 'You earn $1 per completed booking for life. As long as your referred hosts are active, you earn.'
  },
  {
    question: 'When do I get paid?',
    answer: 'Payouts are processed one week after the guest has used the service, aligned with host payouts.'
  },
  {
    question: 'Do I need approval?',
    answer: 'Yes. All affiliate applications are reviewed by our team. Only approved affiliates can access the dashboard and earn commissions.'
  },
  {
    question: 'What if a booking is cancelled?',
    answer: 'Cancelled or refunded bookings do not count towards your earnings. Only completed bookings qualify.'
  }
];

export default function AffiliateLandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <div className="relative min-h-[90vh] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1600&q=80')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/85 to-black/60" />
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full py-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-4 py-2 mb-6">
              <Handshake size={20} className="text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm font-medium">Affiliate Program</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight text-white mb-6">
              Partner with 100Nook.
              <br />
              <span className="text-[#D4AF37]">Refer Hosts. Earn for Life.</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-zinc-300 leading-relaxed mb-8">
              Earn commissions on every completed booking from hosts you refer. 
              Start at $0.50 per booking and unlock up to $1.00 with our Founder Tier. US affiliates only.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => navigate('/affiliate/apply')}
                className="bg-[#D4AF37] text-black hover:bg-[#C4A030] px-8 py-6 text-lg font-semibold"
                data-testid="affiliate-apply-btn"
              >
                Apply to Become Affiliate
                <ArrowRight size={20} className="ml-2" />
              </Button>
              <Button 
                onClick={() => navigate('/affiliate/login')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/5 px-8 py-6 text-lg"
                data-testid="affiliate-login-btn"
              >
                Log In
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Commission Tiers */}
      <div className="py-24 px-6 md:px-12 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-serif text-white mb-4">Commission Tiers</h2>
            <p className="text-zinc-400 text-lg">The more hosts you refer, the more you earn per booking</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { tier: 'Base', hosts: '0-24', rate: '$0.50', color: 'border-zinc-600' },
              { tier: 'Silver', hosts: '25+', rate: '$0.60', color: 'border-gray-400' },
              { tier: 'Gold', hosts: '50+', rate: '$0.70', color: 'border-yellow-500' },
              { tier: 'Platinum', hosts: '100+', rate: '$0.85', color: 'border-blue-400' },
              { tier: 'Founder', hosts: '200+ (in 2 yrs)', rate: '$1.00', color: 'border-[#D4AF37]', highlight: true },
            ].map((item, index) => (
              <div 
                key={index} 
                className={`bg-[#121212] border-2 ${item.color} p-6 text-center ${item.highlight ? 'ring-2 ring-[#D4AF37]/50' : ''}`}
              >
                <div className={`text-sm font-semibold mb-2 ${item.highlight ? 'text-[#D4AF37]' : 'text-zinc-400'}`}>
                  {item.tier}
                </div>
                <div className={`text-3xl font-serif mb-2 ${item.highlight ? 'text-[#D4AF37]' : 'text-white'}`}>
                  {item.rate}
                </div>
                <div className="text-zinc-500 text-sm">per booking</div>
                <div className={`text-xs mt-2 ${item.highlight ? 'text-[#D4AF37]' : 'text-zinc-400'}`}>
                  {item.hosts} hosts
                </div>
                {item.highlight && (
                  <div className="text-[#D4AF37] text-xs mt-2 font-semibold">FOR LIFE!</div>
                )}
              </div>
            ))}
          </div>
          
          <p className="text-center text-zinc-500 mt-8 text-sm">
            Founder Tier requires referring 200 hosts within 2 years of approval. Once achieved, $1.00/booking applies permanently.
          </p>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-serif text-white mb-4">How It Works</h2>
            <p className="text-zinc-400 text-lg">Four simple steps to start earning</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Apply', description: 'Submit your application to become a 100Nook Affiliate' },
              { step: '02', title: 'Get Approved', description: 'Our team reviews and approves qualified applicants' },
              { step: '03', title: 'Share Your Link', description: 'Get your unique referral link and share it with potential hosts' },
              { step: '04', title: 'Earn & Grow', description: 'Earn per booking and unlock higher tiers as you refer more hosts' },
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-[#121212] border border-white/10 p-8 h-full">
                  <div className="text-[#D4AF37] text-5xl font-serif mb-4">{item.step}</div>
                  <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-zinc-400">{item.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-[#D4AF37]">
                    <ArrowRight size={24} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Badges Section */}
      <div className="py-24 px-6 md:px-12 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-serif text-white mb-4">Earn Exclusive Badges</h2>
            <p className="text-zinc-400 text-lg">Get recognized for your impact in local communities</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-[#121212] border border-blue-500/30 p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <MapPin size={24} className="text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-white">State Builder</h3>
              </div>
              <p className="text-zinc-400 mb-4">
                Refer 50+ hosts within a single US state to earn this prestigious badge.
              </p>
              <p className="text-blue-400 text-sm">Example: "Maryland State Builder"</p>
            </div>
            
            <div className="bg-[#121212] border border-green-500/30 p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                  <House size={24} className="text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-white">Local Ambassador</h3>
              </div>
              <p className="text-zinc-400 mb-4">
                Refer 10+ hosts within the same ZIP code to become a local champion.
              </p>
              <p className="text-green-400 text-sm">Example: "Local Ambassador (21201)"</p>
            </div>
          </div>
        </div>
      </div>

      {/* Earnings Example */}
      <div className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-serif text-white mb-6">
                See Your Earning Potential
              </h2>
              <p className="text-zinc-400 text-lg mb-8">
                Your earnings grow with every host you refer. Unlock higher tiers for bigger commissions.
              </p>
              
              <div className="bg-[#121212] border border-white/10 p-8">
                <h3 className="text-lg text-zinc-400 mb-4">Example Scenario:</h3>
                <ul className="space-y-3 text-white">
                  <li className="flex items-center gap-3">
                    <CheckCircle size={24} className="text-[#D4AF37]" weight="fill" />
                    <span>Refer <strong>10 hosts</strong></span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle size={24} className="text-[#D4AF37]" weight="fill" />
                    <span>Each host gets <strong>20 bookings/month</strong></span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle size={24} className="text-[#D4AF37]" weight="fill" />
                    <span>You earn <strong>$0.50 per booking</strong></span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/30 p-10">
              <div className="text-center">
                <p className="text-zinc-400 text-lg mb-2">Your Monthly Earnings</p>
                <div className="text-6xl font-serif text-[#D4AF37] mb-4">
                  $100
                </div>
                <p className="text-zinc-400 text-sm mb-6">10 hosts × 20 bookings × $0.50</p>
                
                <div className="border-t border-[#D4AF37]/30 pt-6">
                  <p className="text-zinc-400 text-lg mb-2">Your Annual Earnings</p>
                  <div className="text-5xl font-serif text-white">
                    $1,200
                  </div>
                  <p className="text-zinc-400 text-sm mt-2">Recurring. Passive. Forever.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Who This Is For */}
      <div className="py-24 px-6 md:px-12 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-serif text-white mb-4">Who This Is For</h2>
            <p className="text-zinc-400 text-lg">Perfect for professionals with access to homeowners</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {AFFILIATE_TYPES.map((type, index) => {
              const Icon = type.icon;
              return (
                <div 
                  key={index}
                  className="bg-[#121212] border border-white/10 p-6 hover:border-[#D4AF37]/50 transition-colors"
                >
                  <Icon size={32} className="text-[#D4AF37] mb-4" weight="duotone" />
                  <h3 className="text-xl font-semibold text-white mb-2">{type.title}</h3>
                  <p className="text-zinc-400">{type.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="py-24 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-serif text-white mb-4">Frequently Asked Questions</h2>
          </div>
          
          <div className="space-y-6">
            {FAQ_ITEMS.map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 p-6"
              >
                <h3 className="text-lg font-semibold text-white mb-2">{item.question}</h3>
                <p className="text-zinc-400">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24 px-6 md:px-12 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-serif text-white mb-6">
            Ready to Start Earning?
          </h2>
          <p className="text-zinc-400 text-lg mb-8">
            Join the 100Nook Affiliate Program and turn your network into recurring income.
          </p>
          <Button 
            onClick={() => navigate('/affiliate/apply')}
            className="bg-[#D4AF37] text-black hover:bg-[#C4A030] px-10 py-6 text-lg font-semibold"
          >
            Apply Now
            <ArrowRight size={20} className="ml-2" />
          </Button>
        </div>
      </div>

      {/* Footer */}
      <div className="py-8 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-zinc-500 text-sm">
            © 2026 100Nook. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
